import React, { useEffect, useState } from 'react';
import styles from './BeaconIndicator.module.css';

interface BeaconIndicatorProps {
  beaconWorldX: number; // Beacon's X position in world coordinates (pixels)
  beaconWorldY: number; // Beacon's Y position in world coordinates (pixels)
  containerRef: React.RefObject<HTMLDivElement>;
}

type EdgePosition = 'top' | 'bottom' | 'left' | 'right';

function BeaconIndicator({ beaconWorldX, beaconWorldY, containerRef }: BeaconIndicatorProps) {
  const [edgeGlow, setEdgeGlow] = useState<{
    edge: EdgePosition;
    position: number; // Percentage along the edge (0-100)
    visible: boolean;
  } | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const updateIndicator = () => {
      const rect = containerRef.current!.getBoundingClientRect();

      // Add a buffer zone - only show indicator when beacon is clearly off-screen
      const buffer = 50; // pixels
      const isInViewport =
        beaconWorldX >= -buffer &&
        beaconWorldX <= rect.width + buffer &&
        beaconWorldY >= -buffer &&
        beaconWorldY <= rect.height + buffer;

      if (isInViewport) {
        setEdgeGlow(null);
        return;
      }

      // Calculate angle from viewport center to beacon
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const dx = beaconWorldX - centerX;
      const dy = beaconWorldY - centerY;
      const angle = Math.atan2(dy, dx);

      let edge: EdgePosition;
      let position: number;

      // Determine which edge based on angle
      // Convert angle to 0-360 degrees
      const degrees = ((angle * 180 / Math.PI) + 360) % 360;

      if (degrees >= 315 || degrees < 45) {
        // Right edge
        edge = 'right';
        // Calculate where the ray intersects the right edge
        const t = (rect.width - centerX) / Math.cos(angle);
        const intersectY = centerY + t * Math.sin(angle);
        position = (intersectY / rect.height) * 100;
      } else if (degrees >= 45 && degrees < 135) {
        // Bottom edge
        edge = 'bottom';
        const t = (rect.height - centerY) / Math.sin(angle);
        const intersectX = centerX + t * Math.cos(angle);
        position = (intersectX / rect.width) * 100;
      } else if (degrees >= 135 && degrees < 225) {
        // Left edge
        edge = 'left';
        const t = -centerX / Math.cos(angle);
        const intersectY = centerY + t * Math.sin(angle);
        position = (intersectY / rect.height) * 100;
      } else {
        // Top edge
        edge = 'top';
        const t = -centerY / Math.sin(angle);
        const intersectX = centerX + t * Math.cos(angle);
        position = (intersectX / rect.width) * 100;
      }

      // Clamp position to 10-90 to keep glow away from corners
      position = Math.max(10, Math.min(90, position));

      setEdgeGlow({
        edge,
        position,
        visible: true
      });
    };

    updateIndicator();

    // Update on resize
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, [beaconWorldX, beaconWorldY, containerRef]);

  if (!edgeGlow?.visible) return null;

  // Create style for edge-specific positioning
  const glowStyle: React.CSSProperties = {
    [edgeGlow.edge]: 0,
    ...(edgeGlow.edge === 'left' || edgeGlow.edge === 'right'
      ? { top: `${edgeGlow.position}%` }
      : { left: `${edgeGlow.position}%` })
  };

  return (
    <div
      className={`${styles.edgeGlow} ${styles[edgeGlow.edge]}`}
      style={glowStyle}
    />
  );
}

export default BeaconIndicator;
